import React, { RefObject } from 'react';
import { useTheme } from 'styled-components';
import { CallToAction } from '../../commonTypes/links';
import { ComponentProps } from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import { Background, ButtonVariant } from '../Button/styles';
import Icon from '../Icon';
import { ButtonStyle, CtaText, CtaTextWrapper, IconWrapper } from './styles';

export type CtaProps = {
  cta: CallToAction;
  icon?: string;
  background?: Background;
  disabled?: boolean;
  innerRef?: RefObject<HTMLAnchorElement>;
  arrowDirection?: 'right' | 'down';
  type?: 'button' | 'submit' | 'reset';
} & ComponentProps;

type GenericCtaProps = {
  variant: ButtonVariant;
} & CtaProps;

const GenericCta: React.FC<GenericCtaProps> = ({
  cta,
  icon,
  type,
  arrowDirection = 'right',
  ...props
}) => {
  const theme = useTheme();

  const screenReaderText = cta.screenReaderText ?? cta.displayText;

  const defaultIcon = cta.openInNewTab
    ? theme.buttonIcons?.ctaOpenInNewTab
    : theme.buttonIcons?.cta;

  const iconToDisplay = icon || defaultIcon;

  return (
    <ButtonStyle
      {...props}
      arrowDirection={arrowDirection}
      type={type}
      href={cta.url}
      onClick={cta.onClick}
      screenReaderText={
        cta.openInNewTab ? `${screenReaderText} - opens in a new tab` : screenReaderText
      }
      openInNewTab={cta.openInNewTab}>
      <CtaTextWrapper>
        <CtaText>{cta.displayText}</CtaText>
      </CtaTextWrapper>
      {iconToDisplay && (
        <IconWrapper>
          <Icon name={iconToDisplay} size="small" />
        </IconWrapper>
      )}
    </ButtonStyle>
  );
};

export const PrimaryCta = applyComponentTheme<CtaProps>(
  (props) => <GenericCta variant="primary" {...props} />,
  'PrimaryCta'
);

export const SecondaryCta = applyComponentTheme<CtaProps>(
  (props) => <GenericCta variant="secondary" {...props} />,
  'SecondaryCta'
);
